import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import ReactJson from '@microlink/react-json-view';
import Select from 'react-select';
import { 
  ChevronDown, 
  ChevronUp, 
  Search, 
  Trash2, 
  RefreshCw,
  Calendar,
  Filter,
  Download,
  Copy,
  Check
} from 'lucide-react';
import { isAdminRole } from '../utils/roleHelpers';

const LogsTab = () => {
  const user = useSelector(state => state.auth.user);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [users, setUsers] = useState([]);
  const [copiedLog, setCopiedLog] = useState(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    userIds: [], // Changed to array for multiple selection
    method: '',
    statusCode: '',
    urlPattern: '',
    role: ''
  });
  
  const [showFilters, setShowFilters] = useState(false);

  // HTTP Status Code options
  const HTTP_STATUS_CODES = [
    { value: '', label: 'All Status Codes' },
    { value: '200', label: '200 - OK' },
    { value: '201', label: '201 - Created' },
    { value: '204', label: '204 - No Content' },
    { value: '301', label: '301 - Moved Permanently' },
    { value: '302', label: '302 - Found' },
    { value: '304', label: '304 - Not Modified' },
    { value: '400', label: '400 - Bad Request' },
    { value: '401', label: '401 - Unauthorized' },
    { value: '403', label: '403 - Forbidden' },
    { value: '404', label: '404 - Not Found' },
    { value: '422', label: '422 - Unprocessable Entity' },
    { value: '429', label: '429 - Too Many Requests' },
    { value: '500', label: '500 - Internal Server Error' },
    { value: '502', label: '502 - Bad Gateway' },
    { value: '503', label: '503 - Service Unavailable' },
    { value: '504', label: '504 - Gateway Timeout' }
  ];

  // HTTP Methods
  const HTTP_METHODS = [
    { value: '', label: 'All Methods' },
    { value: 'GET', label: 'GET' },
    { value: 'POST', label: 'POST' },
    { value: 'PUT', label: 'PUT' },
    { value: 'DELETE', label: 'DELETE' },
    { value: 'PATCH', label: 'PATCH' }
  ];

  // App Roles
  const APP_ROLES = [
    { value: '', label: 'All Roles' },
    { value: 'admin', label: 'Admin' },
    { value: 'instructor', label: 'Instructor' },
    { value: 'student', label: 'Student' }
  ];

  // Fetch logs
  const fetchLogs = async (page = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: page,
        limit: 50,
        sortBy: 'timestamp',
        sortOrder: 'desc'
      });
      
      // Add filters if set
      Object.entries(filters).forEach(([key, value]) => {
        if (key === 'userIds' && Array.isArray(value) && value.length > 0) {
          // Add each userId as a separate parameter
          value.forEach(userId => params.append('userId', userId));
        } else if (value && key !== 'userIds') {
          params.append(key, value);
        }
      });
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || ''}/api/logs?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        setLogs(response.data.data);
        setCurrentPage(response.data.pagination.page);
        setTotalPages(response.data.pagination.totalPages);
        setTotalLogs(response.data.pagination.total);
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
      setError(err.response?.data?.error || 'Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  };

  // Fetch users for filter
  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch all users with a high limit for the filter dropdown
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || ''}/api/users?limit=10000`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      // Handle new pagination format
      const usersData = response.data?.data || response.data || [];
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || ''}/api/logs/stats?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  // Delete log
  const deleteLog = async (logId) => {
    if (!window.confirm('Are you sure you want to delete this log?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL || ''}/api/logs/${logId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Refresh logs
      fetchLogs(currentPage);
      fetchStats();
    } catch (err) {
      console.error('Error deleting log:', err);
      alert('Failed to delete log');
    }
  };

  // Bulk delete old logs
  const bulkDeleteOldLogs = async (days) => {
    if (!window.confirm(`Delete all logs older than ${days} days?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL || ''}/api/logs?olderThanDays=${days}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        alert(response.data.message);
        fetchLogs(1);
        fetchStats();
      }
    } catch (err) {
      console.error('Error bulk deleting logs:', err);
      alert('Failed to bulk delete logs');
    }
  };

  useEffect(() => {
    fetchLogs(1);
    fetchStats();
    fetchUsers();
    // eslint-disable-next-line
  }, []);

  const toggleRowExpanded = (correlationId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(correlationId)) {
      newExpanded.delete(correlationId);
    } else {
      newExpanded.add(correlationId);
    }
    setExpandedRows(newExpanded);
  };

  const copyLogToClipboard = async (log) => {
    try {
      const logData = JSON.stringify(log, null, 2);
      await navigator.clipboard.writeText(logData);
      setCopiedLog(log._id);
      setTimeout(() => setCopiedLog(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Failed to copy to clipboard');
    }
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchLogs(1);
    fetchStats();
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      userIds: [],
      method: '',
      statusCode: '',
      urlPattern: '',
      role: ''
    });
    setCurrentPage(1);
  };

  const getStatusColor = (statusCode) => {
    if (statusCode >= 200 && statusCode < 300) return 'text-green-600 bg-green-100';
    if (statusCode >= 300 && statusCode < 400) return 'text-blue-600 bg-blue-100';
    if (statusCode >= 400 && statusCode < 500) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getMethodColor = (method) => {
    const colors = {
      GET: 'bg-blue-100 text-blue-800',
      POST: 'bg-green-100 text-green-800',
      PUT: 'bg-yellow-100 text-yellow-800',
      DELETE: 'bg-red-100 text-red-800',
      PATCH: 'bg-purple-100 text-purple-800'
    };
    return colors[method] || 'bg-gray-100 text-gray-800';
  };

  if (!user || user.role !== 'platform_admin') {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Access denied. Admin privileges required.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-full">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-[#054581]">Request Logs</h2>
          <div className="flex gap-2">
            <button
              onClick={() => fetchLogs(currentPage)}
              className="flex items-center gap-2 px-4 py-2 bg-[#1086a4] text-white rounded hover:bg-[#054581] transition-colors"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 border border-[#1086a4] text-[#1086a4] rounded hover:bg-[#1086a4] hover:text-white transition-colors"
            >
              <Filter size={16} />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600">Total Requests</p>
              <p className="text-2xl font-bold text-[#054581]">{stats.totalRequests || 0}</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-gray-600">Success Rate</p>
              <p className="text-2xl font-bold text-green-600">
                {stats.totalRequests > 0 
                  ? Math.round((stats.successCount / stats.totalRequests) * 100) 
                  : 0}%
              </p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-gray-600">Error Count</p>
              <p className="text-2xl font-bold text-red-600">{stats.errorCount || 0}</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-gray-600">Avg Response Time</p>
              <p className="text-2xl font-bold text-blue-600">
                {stats.avgResponseTime ? Math.round(stats.avgResponseTime) : 0}ms
              </p>
            </div>
          </div>
        )}

        {/* Filters */}
        {showFilters && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="datetime-local"
                  value={filters.startDate}
                  onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="datetime-local"
                  value={filters.endDate}
                  onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  User
                </label>
                <Select
                  isMulti
                  value={users
                    .filter(u => filters.userIds.includes(u._id))
                    .map(u => ({
                      value: u._id,
                      label: `${u.name} ${u.lastName} (${u.email})`
                    }))}
                  onChange={(selectedOptions) => {
                    const selectedIds = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                    setFilters({...filters, userIds: selectedIds});
                  }}
                  options={users.map(u => ({
                    value: u._id,
                    label: `${u.name} ${u.lastName} (${u.email})`
                  }))}
                  placeholder="Search and select users..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '38px',
                      borderColor: '#d1d5db',
                      '&:hover': {
                        borderColor: '#1086a4'
                      }
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#e0f2fe'
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: '#0c4a6e'
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: '#0c4a6e',
                      '&:hover': {
                        backgroundColor: '#1086a4',
                        color: 'white'
                      }
                    })
                  }}
                  isClearable
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Method
                </label>
                <select
                  value={filters.method}
                  onChange={(e) => setFilters({...filters, method: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                >
                  {HTTP_METHODS.map(method => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status Code
                </label>
                <select
                  value={filters.statusCode}
                  onChange={(e) => setFilters({...filters, statusCode: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                >
                  {HTTP_STATUS_CODES.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL Pattern
                </label>
                <input
                  type="text"
                  value={filters.urlPattern}
                  onChange={(e) => setFilters({...filters, urlPattern: e.target.value})}
                  placeholder="e.g., /api/users"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  value={filters.role}
                  onChange={(e) => setFilters({...filters, role: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                >
                  {APP_ROLES.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={applyFilters}
                className="px-4 py-2 bg-[#1086a4] text-white rounded hover:bg-[#054581] transition-colors"
              >
                Apply Filters
              </button>
              <button
                onClick={() => {
                  clearFilters();
                  setTimeout(() => {
                    fetchLogs(1);
                    fetchStats();
                  }, 0);
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}

        {/* Bulk Actions */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => bulkDeleteOldLogs(7)}
            className="px-3 py-1 text-sm bg-orange-100 text-orange-700 rounded hover:bg-orange-200 transition-colors"
          >
            Delete logs older than 7 days
          </button>
          <button
            onClick={() => bulkDeleteOldLogs(30)}
            className="px-3 py-1 text-sm bg-orange-100 text-orange-700 rounded hover:bg-orange-200 transition-colors"
          >
            Delete logs older than 30 days
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#1086a4]"></div>
          <p className="mt-2 text-gray-600">Loading logs...</p>
        </div>
      )}

      {/* Logs Table */}
      {!loading && logs.length > 0 && (
        <>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Method
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      URL
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Response Time
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.map((log) => (
                    <React.Fragment key={log._id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {new Date(log.timestamp).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {log.user ? (
                            <div>
                              <div className="font-medium text-gray-900">
                                {log.user.name} {log.user.lastName}
                              </div>
                              <div className="text-gray-500 text-xs">{log.auth.role}</div>
                              {log.auth?.isImpersonating && (
                                <div className="text-orange-600 text-xs font-semibold mt-1">
                                  🔄 Impersonated by {log.auth.impersonatorName} {log.auth.impersonatorLastName}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 italic">Unauthenticated</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded ${getMethodColor(log.method)}`}>
                            {log.method}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate" title={log.url}>
                          {log.url}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded ${getStatusColor(log.statusCode)}`}>
                            {log.statusCode}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {Math.round(log.responseTime)}ms
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <div className="flex gap-2">
                            <button
                              onClick={() => toggleRowExpanded(log.correlationId)}
                              className="text-[#1086a4] hover:text-[#054581]"
                              title="View details"
                            >
                              {expandedRows.has(log.correlationId) ? (
                                <ChevronUp size={18} />
                              ) : (
                                <ChevronDown size={18} />
                              )}
                            </button>
                            <button
                              onClick={() => copyLogToClipboard(log)}
                              className="text-[#1086a4] hover:text-[#054581]"
                              title="Copy JSON to clipboard"
                            >
                              {copiedLog === log._id ? (
                                <Check size={16} className="text-green-600" />
                              ) : (
                                <Copy size={16} />
                              )}
                            </button>
                            <button
                              onClick={() => deleteLog(log._id)}
                              className="text-red-600 hover:text-red-800"
                              title="Delete log"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedRows.has(log.correlationId) && (
                        <tr>
                          <td colSpan="7" className="px-4 py-4 bg-gray-50">
                            <div className="space-y-4">
                              <div>
                                <h4 className="font-semibold text-sm text-gray-700 mb-2">Correlation ID</h4>
                                <code className="text-xs bg-white px-2 py-1 rounded border border-gray-200">
                                  {log.correlationId}
                                </code>
                              </div>
                              
                              {log.headers && Object.keys(log.headers).length > 0 && (
                                <div>
                                  <h4 className="font-semibold text-sm text-gray-700 mb-2">Headers</h4>
                                  <ReactJson 
                                    src={log.headers} 
                                    theme="rjv-default"
                                    collapsed={1}
                                    displayDataTypes={false}
                                    enableClipboard={true}
                                    name={false}
                                  />
                                </div>
                              )}
                              
                              {log.requestBody && Object.keys(log.requestBody).length > 0 && (
                                <div>
                                  <h4 className="font-semibold text-sm text-gray-700 mb-2">Request Body</h4>
                                  <ReactJson 
                                    src={log.requestBody} 
                                    theme="rjv-default"
                                    collapsed={1}
                                    displayDataTypes={false}
                                    enableClipboard={true}
                                    name={false}
                                  />
                                </div>
                              )}
                              
                              {log.responseBody && Object.keys(log.responseBody).length > 0 && (
                                <div>
                                  <h4 className="font-semibold text-sm text-gray-700 mb-2">Response Body</h4>
                                  <ReactJson 
                                    src={log.responseBody} 
                                    theme="rjv-default"
                                    collapsed={1}
                                    displayDataTypes={false}
                                    enableClipboard={true}
                                    name={false}
                                  />
                                </div>
                              )}
                              
                              {log.dbOperations && log.dbOperations.length > 0 && (
                                <div>
                                  <h4 className="font-semibold text-sm text-gray-700 mb-2">
                                    Database Operations ({log.dbOperations.length})
                                  </h4>
                                  <div className="space-y-2">
                                    {log.dbOperations.map((op, idx) => (
                                      <div key={idx} className="bg-white p-3 rounded border border-gray-200">
                                        <div className="flex justify-between items-start mb-2">
                                          <span className="font-mono text-xs font-semibold text-[#054581]">
                                            {op.type} on {op.collectionName || op.collection}
                                          </span>
                                          <span className="text-xs text-gray-500">{op.duration}</span>
                                        </div>
                                        
                                        {/* Filter/Conditions */}
                                        <div className="mb-2">
                                          <ReactJson 
                                            src={op.conditions} 
                                            theme="rjv-default"
                                            collapsed={true}
                                            displayDataTypes={false}
                                            enableClipboard={true}
                                            name="conditions"
                                          />
                                        </div>

                                        {/* Result Details */}
                                        <div className="flex flex-wrap gap-3 text-xs mt-2">
                                          {op.resultCount !== undefined && (
                                            <span className="text-gray-600">
                                              <span className="font-semibold">Results:</span> {op.resultCount}
                                            </span>
                                          )}
                                        </div>

                                        {/* Error */}
                                        {op.error && (
                                          <div className="mt-2 p-2 bg-red-50 rounded">
                                            <p className="text-xs text-red-600 font-semibold">
                                              Error: {op.error}
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Showing {logs.length} of {totalLogs} logs
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => fetchLogs(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => fetchLogs(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {/* No Logs */}
      {!loading && logs.length === 0 && (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
          <p className="text-gray-500">No logs found</p>
        </div>
      )}
    </div>
  );
};

export default LogsTab;
